import { useTranslation } from 'react-i18next';
import { cn } from './cn';
import { focusRing } from './focus-ring';

/**
 * Date-strip tile (design-spec §3 "Day tile").
 *
 * Three lines: weekday (11.5/600 uppercase), day number (18/700; 16 mobile),
 * month (11/400). ~100×80 desktop (flex, equal widths), 62×57 mobile.
 * - default  — surface, 1px line; weekday/month label colour; number ink
 * - selected — ink fill, white text (weekday/month at 75%); `aria-pressed`
 * - closed   — disabled fill/text; month line becomes "Closed"; `aria-disabled`,
 *              clicks ignored (stays in the strip so dates don't shift)
 *
 * Callers format the strings in the salon's timezone/locale and pass a full
 * `ariaLabel` ("Wednesday 30 September").
 */
export interface DayTileProps {
  weekday: string;
  day: string | number;
  month: string;
  ariaLabel: string;
  selected?: boolean;
  closed?: boolean;
  size?: 'md' | 'sm';
  /** Mobile board drops the "Closed" text; keep it by default. */
  showClosedLabel?: boolean;
  onSelect?: () => void;
  className?: string;
}

export function DayTile({
  weekday,
  day,
  month,
  ariaLabel,
  selected = false,
  closed = false,
  size = 'md',
  showClosedLabel = true,
  onSelect,
  className,
}: DayTileProps) {
  const { t } = useTranslation();
  const closedText = t('ui.dayTile.closed');
  return (
    <button
      type="button"
      aria-label={closed ? `${ariaLabel}, ${closedText}` : ariaLabel}
      aria-pressed={closed ? undefined : selected}
      aria-disabled={closed || undefined}
      onClick={() => {
        if (!closed) onSelect?.();
      }}
      className={cn(
        'flex flex-col items-center justify-center font-sans leading-tight transition-colors',
        focusRing,
        size === 'md'
          ? 'min-h-[80px] flex-1 rounded-14 py-3'
          : 'h-[57px] w-[62px] shrink-0 rounded-12',
        closed
          ? 'cursor-not-allowed bg-sl-disabled-fill text-sl-disabled-text'
          : selected
          ? 'bg-sl-ink text-white'
          : 'border border-sl-line bg-sl-surface text-sl-ink hover:border-sl-line-strong',
        className
      )}
    >
      <span
        className={cn(
          'text-[11.5px] font-semibold uppercase',
          !closed && (selected ? 'text-white/75' : 'text-sl-label-2')
        )}
      >
        {weekday}
      </span>
      <span
        className={cn(
          'font-bold tabular-nums',
          size === 'md' ? 'text-[18px]' : 'text-[16px]'
        )}
      >
        {day}
      </span>
      {(!closed || showClosedLabel) && (
        <span
          className={cn(
            'text-[11px]',
            !closed && (selected ? 'text-white/75' : 'text-sl-label-2')
          )}
        >
          {closed ? closedText : month}
        </span>
      )}
    </button>
  );
}
